import { Button, DisabledReason, Spinner } from '@hgc/ui';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';

import { useVehicleState } from '../runtime/hooks';
import { MapEngine, type MapSnapshot, type MapSurface } from './engine';
import {
  defaultSourceCatalog,
  readSourceKey,
  sourceReadiness,
  type BaseMapSource,
} from './sources';
import { readFollowPreference, readSourcePreference, writeFollowPreference } from './settings';
import type { VehiclePosition } from './track';
import { FrameIcon, LocateIcon } from '../app/icons';

/**
 * 飞行分区的地图。
 *
 * 与计划分区的 `MapPanel` 是**两件事**，所以是两个组件而不是一个带 `variant` 的组件：
 * 计划页要的是"编辑航线时看得见底图"，因此它带着底图源选择、离线开关、缓存统计
 * 一整排设置；飞行页要的是"此刻飞机在哪、往哪飞"，因此它只有跟随/全览两个动作
 * 和一行状态。硬把它们合成一个组件，得到的是一个每个分区都只用到一半的巨型组件，
 * 而且"飞行页为什么会出现瓦片缓存统计"这种问题会永远解释不清。
 *
 * 两者共用 `engine.ts`（相机与数据）与 `MapCanvas.tsx`（MapLibre 适配）：那才是真正的复用点。
 *
 * MapLibre 约 1 MB，按需加载：飞行页是首屏，不该为一个还没滚到的地方付这份代价。
 */
const MapCanvas = lazy(async () => {
  const module = await import('./MapCanvas');
  return { default: module.MapCanvas };
});

export function FlyMap(): JSX.Element {
  const state = useVehicleState();
  const sources = useMemo(() => defaultSourceCatalog.list(), []);
  const [surface, setSurface] = useState<MapSurface | null>(null);
  const [snapshot, setSnapshot] = useState<MapSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const engineRef = useRef<MapEngine | null>(null);
  /** 状态放进 ref 给引擎用：地图不该因为"又收到一帧"而重建 */
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (surface === null) return undefined;
    const source = pickSource(sources);
    const engine = new MapEngine({ surface, source, follow: readFollowPreference() });
    engineRef.current = engine;
    engine.update(stateRef.current);
    setSnapshot(engine.snapshotOf());
    const unsubscribe = engine.subscribe(() => setSnapshot(engine.snapshotOf()));
    return () => {
      unsubscribe();
      engine.dispose();
      engineRef.current = null;
    };
  }, [surface, sources]);

  useEffect(() => {
    engineRef.current?.update(state);
  }, [state]);

  const onUserGesture = useCallback((originalEvent: unknown) => {
    engineRef.current?.onMapMove(originalEvent);
  }, []);

  const toggleFollow = useCallback(() => {
    const engine = engineRef.current;
    if (engine === null) return;
    const next = !(snapshot?.follow.mode === 'following');
    engine.setFollow(next);
    writeFollowPreference(next);
  }, [snapshot]);

  const following = snapshot?.follow.mode === 'following';
  const position = snapshot?.position;

  return (
    <div className="relative flex min-h-[320px] min-w-0 flex-1 flex-col overflow-hidden rounded-card border border-line bg-surface-2 shadow-card">
      {error === null ? (
        <Suspense fallback={<MapPlaceholder text="地图组件加载中…" />}>
          <MapCanvas
            className="min-h-[320px] flex-1"
            onSurface={setSurface}
            onUserGesture={onUserGesture}
            onError={setError}
          />
        </Suspense>
      ) : (
        /*
         * 地图起不来时必须说清原因，而不是留一块空白 —— 空白会被读成"这里没有内容"，
         * 而真正的问题是"这个浏览器/机器渲染不了地图"。
         */
        <div
          role="alert"
          className="flex flex-1 items-center gap-2 bg-critical-soft px-3 py-2 text-xs text-critical"
        >
          {error}
        </div>
      )}

      {/* 地图控件浮在地图之上：`absolute` 而不是参与布局，才不会把地图挤小 */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-2">
        <div className="pointer-events-auto flex items-center gap-2">
          <DisabledReason reason={snapshot?.canLocate === false ? '尚未收到位置数据' : undefined}>
            <Button
              size="sm"
              variant="secondary"
              iconLeft={<LocateIcon size={14} />}
              onClick={() => engineRef.current?.locate()}
            >
              定位到机体
            </Button>
          </DisabledReason>

          <Button
            size="sm"
            variant={following ? 'primary' : 'secondary'}
            aria-pressed={following}
            iconLeft={<FrameIcon size={14} />}
            onClick={toggleFollow}
          >
            {following ? '跟随中' : '跟随'}
          </Button>

          <Button
            size="sm"
            variant="secondary"
            disabled={snapshot?.canOverview === false}
            onClick={() => engineRef.current?.overview()}
          >
            全览航迹
          </Button>
        </div>

        <div className="pointer-events-auto rounded-control border border-line bg-surface-1/90 px-2 py-1 text-[11px] text-fg-muted backdrop-blur-sm">
          {describeMap(snapshot, position, error)}
        </div>
      </div>
    </div>
  );
}

function MapPlaceholder({ text }: { readonly text: string }): JSX.Element {
  return (
    <div className="flex flex-1 items-center justify-center gap-2 bg-surface-2 text-xs text-fg-subtle">
      <Spinner size="sm" />
      {text}
    </div>
  );
}

/**
 * 地图右下角那一行状态。
 *
 * 刻意把"跟随时为什么会抖/为什么不动"写成一句人话（`hint` 由引擎给出），
 * 因为这正是用户会怀疑"地图坏了"的时刻。
 */
function describeMap(
  snapshot: MapSnapshot | null,
  position: VehiclePosition | undefined,
  error: string | null,
): string {
  if (error !== null) return '地图不可用';
  if (snapshot === null) return '正在初始化地图';
  if (!snapshot.ready) return '底图加载中';
  if (position === undefined || position.point === null) {
    return position?.note ?? '尚未收到位置';
  }
  const parts = [`航迹 ${snapshot.trailCount} 点 · ${formatMeters(snapshot.trailLengthM)}`];
  if (snapshot.hint !== null) parts.push(snapshot.hint);
  return parts.join(' · ');
}

function formatMeters(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0 m';
  return value >= 1000 ? `${(value / 1000).toFixed(2)} km` : `${value.toFixed(0)} m`;
}

/**
 * 选底图。
 *
 * 复用**用户在计划分区选过的那个源**（`readSourcePreference`）：两个分区各记一份
 * 会让"我在计划页换成了天地图，飞行页还是 OSM"变成一件需要解释的事。
 *
 * 但飞行页比计划页多一道检查：计划页有底图源选择器，用户能在那里看见"这个源需要密钥"
 * 并当场填；飞行页没有那个选择器，所以选到"需要密钥但没填"的源时**退回第一个可用的**。
 * 不退回的话，飞行页会是一块因为一个用户在这里看不见、也改不了的设置而空掉的区域 ——
 * 而"飞机在哪"是这一屏最不能缺的东西。缺密钥这件事在计划页仍然是明确说出来的。
 */
function pickSource(sources: readonly BaseMapSource[]): BaseMapSource {
  const usable = (source: BaseMapSource): boolean =>
    sourceReadiness(source, readSourceKey(source.id)).ready;

  const preferredId = readSourcePreference(sources);
  const preferred = sources.find((source) => source.id === preferredId);
  const chosen = preferred !== undefined && usable(preferred) ? preferred : sources.find(usable);
  const fallback = chosen ?? sources[0];
  if (fallback === undefined) throw new Error('底图源目录是空的：这里应该有内置的 OSM 兜底');
  return fallback;
}
